import { useEffect, useState } from "react";

import styles from "./ClockCard.module.css";

const TIME_FORMAT = {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
} as const;

function formatTime(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-AU", { ...TIME_FORMAT, timeZone }).format(date);
}

function getTimeParts(
  date: Date,
  timeZone: string
): { hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone,
  }).formatToParts(date);

  const valueFor = (type: "hour" | "minute" | "second") => {
    const value = parts.find((part) => part.type === type)?.value ?? "0";
    return Number.parseInt(value, 10);
  };

  return {
    hour: valueFor("hour"),
    minute: valueFor("minute"),
    second: valueFor("second"),
  };
}

/** A small utility card with live clocks for key operating regions. */
export function ClockCard() {
  const [now, setNow] = useState(() => new Date());

  const clocks = [
    { label: "Sydney", timeZone: "Australia/Sydney" },
    { label: "Philippines", timeZone: "Asia/Manila" },
  ] as const;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className={styles.card} aria-label="Regional clocks">
      <p className={styles.eyebrow}>Regional time</p>
      <ul className={styles.grid}>
        {clocks.map((clock) => {
          const { hour, minute, second } = getTimeParts(now, clock.timeZone);
          const secondDeg = second * 6;
          const minuteDeg = minute * 6 + second * 0.1;
          const hourDeg = ((hour % 12) + minute / 60) * 30;

          return (
            <li className={styles.tile} key={clock.timeZone}>
              <span className={styles.city}>{clock.label}</span>
              <div className={styles.face} aria-hidden="true">
                <span
                  className={`${styles.hand} ${styles.hour}`}
                  style={{ transform: `rotate(${hourDeg}deg)` }}
                />
                <span
                  className={`${styles.hand} ${styles.minute}`}
                  style={{ transform: `rotate(${minuteDeg}deg)` }}
                />
                <span
                  className={`${styles.hand} ${styles.second}`}
                  style={{ transform: `rotate(${secondDeg}deg)` }}
                />
                <span className={styles.pivot} />
              </div>
              <time className={styles.time} dateTime={now.toISOString()}>
                {formatTime(now, clock.timeZone)}
              </time>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
